import { Inject, Injectable, LOCALE_ID, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { User } from '../../models/user';
import { BehaviorSubject, forkJoin, from, Observable, of } from 'rxjs';
import { Token } from '../../models/token';
import { catchError, map } from 'rxjs/operators';
import { CasService } from './cas-service';
import { FormioToken } from '../../models/formio-token';
import { Router } from '@angular/router';
import { routerUrlToAbsolute } from '../../utils/url-utils';
import { SESSION_STORAGE_KEYS } from '../../constants/storage-keys';
import {
  HOMEPAGE_ACCESS_ROLES,
  ROLE_PARTB,
  ROLE_SUPER_MARIO
} from '../../constants/roles';

@Injectable()
export class AuthService {
  public keycloakActive: boolean = environment.KEYCLOAK_ACTIVE;
  public iframeMode: boolean = environment.IS_IFRAME_APP;
  public static readonly neededRole: string = 'BPA';
  public currentUser$ = new BehaviorSubject<User | null>(null);
  public isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public jwt$ = new BehaviorSubject<string | null>(null);

  constructor(
    @Inject(LOCALE_ID) private readonly locale: string,
    @Optional() public readonly keycloak: KeycloakService,
    @Optional() public readonly cas: CasService,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {
    const existingToken = localStorage.getItem(Token.tokenNameInStorage);

    if (
      this.iframeMode &&
      this.keycloakActive &&
      this.keycloak &&
      existingToken
    ) {
      const kcInstance = this.keycloak.getKeycloakInstance();
      kcInstance.token = existingToken;
    }

    if (this.keycloakActive && this.keycloak) {
      this.keycloak
        .loadUserProfile()
        .then((keycloakProfile: KeycloakProfile) => {
          let user = new User();
          user.userName = keycloakProfile.username;
          user.first_name = keycloakProfile.firstName;
          user.last_name = keycloakProfile.lastName;
          user.email = keycloakProfile.email;
          user.emailVerified = keycloakProfile.emailVerified;
          this.keycloak.getToken().then((token) => {
            localStorage.setItem(Token.tokenNameInStorage, token);
            localStorage.setItem(
              FormioToken.tokenNameInStorage,
              FormioToken.scheme + token
            );
            localStorage.setItem(
              Token.refreshTokenNameInStorage,
              this.keycloak.getKeycloakInstance().refreshToken || ''
            );
            this.jwt$.next(token);
          });
          this.currentUser$.next(user);
          this.isLoggedIn$.next(true);
        })
        .catch((err) => {
          console.error('Cannot login', err);
          this.logout(window.location.origin).subscribe({
            next: () => {
              if (!environment.LOGOUT_REDIRECT_URL) {
                this.login(false);
              } else {
                window.location.href = environment.LOGOUT_REDIRECT_URL;
              }
            },
            error: (err) => {
              console.error('Can not logout', err);
            }
          });
        });
    } else {
      this.cas.jwt$.subscribe((token) => {
        if (token) {
          this.currentUser$.next(this.cas.loadUserProfile(token));
          this.isLoggedIn$.next(true);
          this.jwt$.next(token);
        } else {
          this._nullBehaviourSubjectsAndNukeLocalStorageCache();
        }
      });
    }
  }

  private _nullBehaviourSubjectsAndNukeLocalStorageCache() {
    this.isLoggedIn$.next(false);
    this.jwt$.next(null);
    this.currentUser$.next(null);
    localStorage.removeItem(Token.tokenNameInStorage);
    localStorage.removeItem(FormioToken.tokenNameInStorage);
    localStorage.removeItem(Token.refreshTokenNameInStorage);
  }

  public logout(redirectUrl: string): Observable<void> {
    // Always clear local state
    this._nullBehaviourSubjectsAndNukeLocalStorageCache();

    if (this.keycloakActive) {
      return from(this.keycloak.logout(redirectUrl)).pipe(
        map(() => {
          // Local state already cleared above
        })
      );
    } else {
      return this.cas.logout().pipe(
        map(() => {
          // Local state already cleared above
        })
      );
    }
  }

  public async login(isRegister: boolean = false, redirectPath?: string) {
    if (this.keycloakActive) {
      // target may be a browser path (with base href, e.g. /parta/services/123)
      // or a router URL (without, e.g. /services/123) — routerUrlToAbsolute handles both
      let target = this.router.url;
      try {
        const saved = sessionStorage.getItem(
          SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT
        );
        if (saved && saved.startsWith('/') && !saved.startsWith('//')) {
          target = saved;
        }
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT);
      } catch (e) {
        // sessionStorage may be unavailable in incognito/restricted contexts
      }
      return await this.keycloak.login({
        redirectUri: routerUrlToAbsolute(target),
        locale: this.locale
      });
    }
    return this.cas.login(isRegister, redirectPath ?? this.router.url);
  }

  /**
   * Checks if the user is already authenticated with Keycloak from another service
   * and attempts to automatically log them in if they are.
   * @returns Promise<boolean> indicating if the user was automatically logged in
   */
  public async checkSsoAndLogin(): Promise<boolean> {
    if (this.keycloakActive) {
      return this.checkKeycloakSsoAndLogin();
    }

    if (this.cas) {
      return this.checkCasSsoAndLogin();
    }

    return false;
  }

  /**
   * Loads Keycloak user profile and updates local state
   * @returns Promise<boolean> indicating if profile was loaded successfully
   */
  private async loadKeycloakUserProfile(): Promise<boolean> {
    try {
      const keycloakProfile = await this.keycloak.loadUserProfile();
      const user = new User();
      user.first_name = keycloakProfile.firstName;
      user.last_name = keycloakProfile.lastName;
      user.email = keycloakProfile.email;
      user.emailVerified = keycloakProfile.emailVerified;

      const token = await this.keycloak.getToken();
      localStorage.setItem(Token.tokenNameInStorage, token);
      localStorage.setItem(
        FormioToken.tokenNameInStorage,
        FormioToken.scheme + token
      );
      localStorage.setItem(
        Token.refreshTokenNameInStorage,
        this.keycloak.getKeycloakInstance().refreshToken || ''
      );

      this.jwt$.next(token);
      this.currentUser$.next(user);
      this.isLoggedIn$.next(true);

      return true;
    } catch (err) {
      console.error('Error loading user profile during auto-login', err);
      return false;
    }
  }

  /**
   * Checks Keycloak SSO status and attempts auto-login
   * @returns Promise<boolean> indicating if auto-login was successful
   */
  private async checkKeycloakSsoAndLogin(): Promise<boolean> {
    if (!this.keycloak) {
      return false;
    }

    try {
      const authenticated = this.keycloak.isLoggedIn();

      if (authenticated) {
        return await this.loadKeycloakUserProfile();
      }

      return await this.tryKeycloakTokenRefresh();
    } catch (err) {
      console.error('Error during auto-login check', err);
      return false;
    }
  }

  /**
   * Attempts to refresh Keycloak token
   * @returns Promise<boolean> indicating if refresh was successful
   */
  private async tryKeycloakTokenRefresh(): Promise<boolean> {
    try {
      const refreshed = await this.keycloak.updateToken(10);

      if (refreshed) {
        // If token was refreshed, user has an active session
        return await this.checkSsoAndLogin();
      }
    } catch (error) {
      console.error('Error checking for existing session:', error);
    }

    return false;
  }

  /**
   * Checks CAS SSO status and attempts auto-login
   * @returns Promise<boolean> indicating if auto-login was successful
   */
  private async checkCasSsoAndLogin(): Promise<boolean> {
    try {
      // Check for existing token
      const existingToken = localStorage.getItem(Token.tokenNameInStorage);
      if (existingToken) {
        return this.isLoggedIn$.value;
      }

      // Try refresh token based silent login
      const refreshToken = localStorage.getItem(
        Token.refreshTokenNameInStorage
      );
      if (refreshToken) {
        return await this.attemptCasTokenRefresh(refreshToken);
      }

      // Attempt SSO redirect if coming from external referrer
      this.attemptCasAutoLoginRedirect();

      return false;
    } catch (err) {
      console.error('Error during CAS auto-login check', err);
      return false;
    }
  }

  /**
   * Attempts to refresh CAS token
   * @param refreshToken The refresh token to use
   * @returns Promise<boolean> indicating if refresh was successful
   */
  private async attemptCasTokenRefresh(refreshToken: string): Promise<boolean> {
    this.cas.updateTokenSubject.next(refreshToken);

    // Wait briefly for login state to update
    return new Promise<boolean>((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(false);
        }
      }, 4000);

      const sub = this.isLoggedIn$.subscribe((val) => {
        if (val && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          sub.unsubscribe();
          resolve(true);
        }
      });
    });
  }

  /**
   * Attempts one-time auto-login redirect to CAS if user came from external domain
   */
  private attemptCasAutoLoginRedirect(): void {
    try {
      const ref = document.referrer;
      if (!ref) {
        return;
      }

      const attemptedKey = 'CAS_AUTOLOGIN_ATTEMPTED';
      const hasAttempted = sessionStorage.getItem(attemptedKey) === '1';
      if (hasAttempted) {
        return;
      }

      const isSameOriginReferrer = this.isSameOriginReferrer(ref);
      if (isSameOriginReferrer) {
        return;
      }

      sessionStorage.setItem(attemptedKey, '1');
      // Trigger CAS authorization flow; if SSO cookie exists, CAS should redirect back with a code.
      this.cas.login(false, this.router.url);
    } catch (e) {
      // Ignore any failures in referrer/origin checks and fall back to no-op
    }
  }

  /**
   * Checks if referrer is from the same origin
   * @param referrer The referrer URL to check
   * @returns boolean indicating if referrer is same origin
   */
  private isSameOriginReferrer(referrer: string): boolean {
    try {
      return new URL(referrer).origin === window.location.origin;
    } catch (e) {
      // If parsing fails, treat as cross-origin to be conservative
      return false;
    }
  }

  /**
   * Introspects a token with the Keycloak server
   * @param token The JWT token to introspect
   * @returns Observable<boolean> indicating if the token is active
   */
  public introspectToken(token: string): Observable<boolean> {
    const baseUrl = environment.KEYCLOAK_URL.endsWith('/')
      ? environment.KEYCLOAK_URL.slice(0, -1)
      : environment.KEYCLOAK_URL;

    const endpoint = `${baseUrl}/realms/${environment.KEYCLOAK_REALM}/protocol/openid-connect/token/introspect`;

    const formData = new URLSearchParams();
    formData.set('token', token);

    const idToken = this.keycloak?.getKeycloakInstance()?.idToken;
    if (idToken) {
      formData.set('id_token_hint', idToken);
    }

    const authHeader =
      'Basic ' +
      btoa(`${environment.OAUTH_CLIENT_ID}:${environment.OAUTH_SECRET}`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: authHeader
    });

    return this.http
      .post<any>(endpoint, formData.toString(), {
        headers: headers
      })
      .pipe(
        map((response) => {
          return response?.active === true;
        }),
        catchError((error) => {
          console.error('Token introspection failed:', error);
          return of(false);
        })
      );
  }

  /**
   * Checks if the current user has a specific role
   * For Keycloak: Checks realm_access and resource_access roles in JWT token
   * For CAS: Delegates to CasService which fetches roles from Part C API
   * @param role The role name to check
   * @returns Observable<boolean> indicating if user has the role
   * TOBE-15453: Fix hasAdvancedRights() for CAS users
   */
  public isUserInRole(role: string): Observable<boolean> {
    if (this.keycloakActive && this.keycloak) {
      // Roles are parsed into the Keycloak instance at init/refresh time
      // (kc.realmAccess.roles and kc.resourceAccess.<client>.roles). Read
      // them synchronously — do NOT call getToken() first.
      //
      // getToken() triggers keycloak-angular's internal updateToken(), which
      // has a TOCTOU race in keycloak-js 25.0.6 (keycloak.mjs:631 sync guard
      // vs :651 async use of kc.refreshToken). When the race fires the body
      // is built with `refresh_token=undefined`, Keycloak responds 400, and
      // the SDK calls kc.clearToken() — silently logging the user out. For a
      // read-only role check we don't need a fresh token; if it is genuinely
      // expired the next API call will refresh it, where refresh belongs.
      try {
        const userRoles = this.keycloak.getUserRoles(true);
        return of(userRoles.includes(role));
      } catch (error) {
        console.error('Error checking Keycloak role:', error);
        return of(false);
      }
    } else if (this.cas) {
      // CAS: Delegate to CasService which fetches from Part C API
      return this.cas.isUserInRole(role);
    }

    return of(false);
  }

  /**
   * True when the current user should see only Part B services where they
   * have at least one role assigned. False when the user has `super_mario`
   * (full back-office access) or lacks `partb` entirely.
   *
   * Predicate: `partb AND NOT super_mario`. Used by the Part B services
   * filter to add the `onlyMyRoles=1` query parameter. Decoupled from the
   * homepage-access decision: a user can be a Part B operator (filtered)
   * AND have homepage access (no redirect, sees the Applicant link). See
   * `hasHomepageAccess$` for the homepage-access predicate.
   *
   * @returns Observable<boolean> emitting the predicate result once and completing.
   */
  public restrictPartBToOwnServices$(): Observable<boolean> {
    // forkJoin is correct here: isUserInRole emits once and completes.
    // combineLatest would also work but never closes.
    return forkJoin([
      this.isUserInRole(ROLE_PARTB),
      this.isUserInRole(ROLE_SUPER_MARIO)
    ]).pipe(map(([isPartB, isSuperMario]) => isPartB && !isSuperMario));
  }

  /**
   * True when the current user holds any role in `HOMEPAGE_ACCESS_ROLES`.
   *
   * Used by:
   *   - `PartBLandingGuard` to skip the auto-redirect from `/` to `/part-b`
   *     (Part B operators with homepage responsibilities, e.g. Lesotho2
   *     registrars holding the `Dashboard` role, stay on `/` instead of
   *     being bounced).
   *   - The user menu, to show the Applicant link (route to `/`).
   *
   * Distinct from `restrictPartBToOwnServices$`: a Lesotho2 registrar
   * (`Dashboard` role) is
   * a Part B operator AND has homepage access — the two predicates carry
   * different intents and should not be conflated.
   *
   * @returns Observable<boolean> emitting once and completing.
   */
  public hasHomepageAccess$(): Observable<boolean> {
    if (HOMEPAGE_ACCESS_ROLES.length === 0) {
      return of(false);
    }
    return forkJoin(
      HOMEPAGE_ACCESS_ROLES.map((role) => this.isUserInRole(role))
    ).pipe(map((results) => results.some((hasRole) => hasRole)));
  }
}
